import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Save, ShieldCheck, Phone, Mail, Check, BadgeCheck, Briefcase, Coins } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { api, formatApiError } from "@/lib/api";
import { SERVICE_LANES } from "@/lib/services";
import { INVESTOR_INTEREST_TAGS, RISK_PROFILES, HORIZONS } from "@/lib/investor";
import { FIJI_ISLANDS } from "@/lib/businesses";
import { Link } from "react-router-dom";

export default function Profile() {
    const { user, updateUser } = useAuth();
    const [services, setServices] = useState([]);
    const [saving, setSaving] = useState(false);
    const [agentForm, setAgentForm] = useState({
        agency_name: "",
        license_no: "",
        agent_bio: "",
        agency_website: "",
    });
    const [savingAgent, setSavingAgent] = useState(false);
    const [investorForm, setInvestorForm] = useState({
        capital_min_fjd: "",
        capital_max_fjd: "",
        interests: [],
        risk_profile: "balanced",
        investment_horizon: "medium",
        accredited: false,
        geo_focus: [],
        investor_notes: "",
    });
    const [savingInvestor, setSavingInvestor] = useState(false);

    useEffect(() => {
        if (user?.services) setServices(user.services);
        if (user) {
            setAgentForm({
                agency_name: user.agency_name || "",
                license_no: user.license_no || "",
                agent_bio: user.agent_bio || "",
                agency_website: user.agency_website || "",
            });
            setInvestorForm({
                capital_min_fjd: user.capital_min_fjd != null ? String(user.capital_min_fjd) : "",
                capital_max_fjd: user.capital_max_fjd != null ? String(user.capital_max_fjd) : "",
                interests: user.interests || [],
                risk_profile: user.risk_profile || "balanced",
                investment_horizon: user.investment_horizon || "medium",
                accredited: !!user.accredited,
                geo_focus: user.geo_focus || [],
                investor_notes: user.investor_notes || "",
            });
        }
    }, [user]);

    const toggle = (id) =>
        setServices((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

    const dirty = (() => {
        const a = [...(user?.services || [])].sort().join(",");
        const b = [...services].sort().join(",");
        return a !== b;
    })();

    const save = async () => {
        if (services.length === 0) {
            toast.error("Pick at least one lane.");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.patch("/auth/services", { services });
            updateUser(data);
            toast.success("Profile updated.");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not save");
        } finally {
            setSaving(false);
        }
    };

    const saveAgent = async () => {
        setSavingAgent(true);
        try {
            const { data } = await api.patch("/agent/profile", agentForm);
            updateUser(data);
            toast.success("Agent profile saved.");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not save agent profile");
        } finally {
            setSavingAgent(false);
        }
    };

    const isAgent = services.includes("agent") || user?.services?.includes("agent");
    const isInvestor = services.includes("investor") || services.includes("business_buyer") || user?.services?.includes("investor") || user?.services?.includes("business_buyer");

    const toggleInterest = (id) =>
        setInvestorForm((f) => ({ ...f, interests: f.interests.includes(id) ? f.interests.filter(x => x !== id) : [...f.interests, id] }));
    const toggleGeo = (island) =>
        setInvestorForm((f) => ({ ...f, geo_focus: f.geo_focus.includes(island) ? f.geo_focus.filter(x => x !== island) : [...f.geo_focus, island] }));

    const saveInvestor = async () => {
        setSavingInvestor(true);
        try {
            const payload = {
                ...investorForm,
                capital_min_fjd: investorForm.capital_min_fjd ? parseFloat(investorForm.capital_min_fjd) : null,
                capital_max_fjd: investorForm.capital_max_fjd ? parseFloat(investorForm.capital_max_fjd) : null,
            };
            const { data } = await api.patch("/investor/profile", payload);
            updateUser(data);
            toast.success("Investor profile saved.");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not save");
        } finally {
            setSavingInvestor(false);
        }
    };

    if (!user) return null;

    return (
        <div data-testid="profile-page" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <div className="mb-8">
                <div className="text-xs uppercase tracking-[0.3em] text-coral font-app font-semibold mb-1">Profile</div>
                <h1 className="font-display text-4xl font-bold">{user.full_name}</h1>
                <p className="text-muted-foreground font-app mt-1">Manage your account, service lanes, and verification.</p>
            </div>

            <div className="grid lg:grid-cols-3 gap-6 mb-8">
                <Card className="p-5">
                    <Mail className="h-4 w-4 text-ocean dark:text-turquoise mb-2" />
                    <div className="text-xs uppercase tracking-wider text-muted-foreground font-app">Email</div>
                    <div className="font-app text-sm font-medium truncate">{user.email}</div>
                </Card>
                <Card className="p-5">
                    <Phone className="h-4 w-4 text-coral mb-2" />
                    <div className="text-xs uppercase tracking-wider text-muted-foreground font-app">Phone</div>
                    <div className="flex items-center gap-2">
                        <span className="font-app text-sm font-medium">{user.phone || "—"}</span>
                        {user.phone_verified ? (
                            <Badge className="bg-fiji-green/15 text-fiji-green border-0 text-[10px] uppercase">
                                <Check className="h-3 w-3 mr-0.5" /> Verified
                            </Badge>
                        ) : (
                            <Link to="/verify-phone" className="text-xs font-app text-coral hover:underline">Verify →</Link>
                        )}
                    </div>
                </Card>
                <Card className="p-5">
                    <ShieldCheck className="h-4 w-4 text-fiji-green mb-2" />
                    <div className="text-xs uppercase tracking-wider text-muted-foreground font-app">Active lanes</div>
                    <div className="font-display text-2xl font-bold">{user.services?.length || 0}</div>
                </Card>
            </div>

            <Card className="p-6">
                <h2 className="font-display text-xl font-bold mb-1">Service lanes</h2>
                <p className="text-sm text-muted-foreground font-app mb-5">
                    Toggle which parts of the marketplace you want active. Adding a lane unlocks its tools in your dashboard;
                    removing one hides them (your data stays — just toggle back on anytime).
                </p>

                <div className="space-y-2" data-testid="profile-services-list">
                    {SERVICE_LANES.map((s) => {
                        const selected = services.includes(s.id);
                        const Icon = s.icon;
                        return (
                            <label
                                key={s.id}
                                data-testid={`profile-service-${s.id}`}
                                className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition ${
                                    selected
                                        ? "border-ocean bg-ocean/5 dark:border-turquoise dark:bg-turquoise/5"
                                        : "border-border hover:border-muted-foreground/40"
                                }`}
                            >
                                <Checkbox checked={selected} onCheckedChange={() => toggle(s.id)} className="mt-0.5" />
                                <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${s.accent}`} />
                                <div className="min-w-0 flex-1">
                                    <div className="font-app font-medium text-sm">{s.label}</div>
                                    <div className="text-xs text-muted-foreground font-app">{s.desc}</div>
                                </div>
                            </label>
                        );
                    })}
                </div>

                <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-xs font-app text-muted-foreground">
                        {dirty ? "Unsaved changes." : "All changes saved."}
                    </div>
                    <Button onClick={save} disabled={saving || !dirty} data-testid="save-services-btn" className="bg-ocean-green text-white hover:opacity-90">
                        <Save className="h-4 w-4 mr-2" />
                        {saving ? "Saving…" : "Save changes"}
                    </Button>
                </div>
            </Card>

            {isAgent && (
                <Card className="p-6 mt-6" data-testid="agent-profile-card">
                    <div className="flex items-center gap-2 mb-1">
                        <Briefcase className="h-5 w-5 text-ocean dark:text-turquoise" />
                        <h2 className="font-display text-xl font-bold">Agent / Agency profile</h2>
                        {user.agent_verified ? (
                            <Badge className="bg-fiji-green/15 text-fiji-green border-0 text-[10px] uppercase font-app">
                                <BadgeCheck className="h-3 w-3 mr-0.5" /> Verified
                            </Badge>
                        ) : (
                            <Badge className="bg-secondary text-muted-foreground border-0 text-[10px] uppercase font-app">
                                Pending verification
                            </Badge>
                        )}
                    </div>
                    <p className="text-sm text-muted-foreground font-app mb-5">
                        These details appear on your public agent profile and on every listing card you upload. Verification is manual — once admin approves, a green badge shows on all your listings.
                    </p>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="agency_name">Agency name</Label>
                            <Input id="agency_name" data-testid="agent-agency-name" value={agentForm.agency_name} onChange={(e) => setAgentForm((f) => ({ ...f, agency_name: e.target.value }))} placeholder="Fiji Pacific Realty" className="mt-1.5" />
                        </div>
                        <div>
                            <Label htmlFor="license_no">License #</Label>
                            <Input id="license_no" data-testid="agent-license-no" value={agentForm.license_no} onChange={(e) => setAgentForm((f) => ({ ...f, license_no: e.target.value }))} placeholder="REA/2025/0001" className="mt-1.5" />
                        </div>
                        <div className="sm:col-span-2">
                            <Label htmlFor="agency_website">Agency website</Label>
                            <Input id="agency_website" data-testid="agent-website" value={agentForm.agency_website} onChange={(e) => setAgentForm((f) => ({ ...f, agency_website: e.target.value }))} placeholder="https://your-agency.com" className="mt-1.5" />
                        </div>
                        <div className="sm:col-span-2">
                            <Label htmlFor="agent_bio">Short bio</Label>
                            <Textarea id="agent_bio" data-testid="agent-bio" value={agentForm.agent_bio} onChange={(e) => setAgentForm((f) => ({ ...f, agent_bio: e.target.value }))} placeholder="15 years selling Fiji land. Specialise in beachfront and tourism-zoned blocks on Viti Levu." className="mt-1.5" rows={3} />
                        </div>
                    </div>
                    <div className="mt-5 flex justify-end">
                        <Button onClick={saveAgent} disabled={savingAgent} data-testid="save-agent-profile-btn" className="bg-ocean-green text-white">
                            <Save className="h-4 w-4 mr-2" />
                            {savingAgent ? "Saving…" : "Save agent profile"}
                        </Button>
                    </div>
                </Card>
            )}

            {isInvestor && (
                <Card className="p-6 mt-6" data-testid="investor-profile-card">
                    <div className="flex items-center gap-2 mb-1">
                        <Coins className="h-5 w-5 text-coral" />
                        <h2 className="font-display text-xl font-bold">Investor capital profile</h2>
                        {investorForm.accredited && (
                            <Badge className="bg-coral/15 text-coral border-0 text-[10px] uppercase font-app">
                                <BadgeCheck className="h-3 w-3 mr-0.5" /> Accredited
                            </Badge>
                        )}
                    </div>
                    <p className="text-sm text-muted-foreground font-app mb-5">
                        Tell us your capital range, what kind of deals you're after, and which islands you want to focus on. The
                        admin team uses this to match you to vetted Joint Ventures, land, and businesses before they go public.
                    </p>

                    <div className="grid sm:grid-cols-2 gap-4 mb-5">
                        <div>
                            <Label>Min capital (FJ$)</Label>
                            <Input data-testid="investor-min" type="number" value={investorForm.capital_min_fjd} onChange={(e) => setInvestorForm(f => ({ ...f, capital_min_fjd: e.target.value }))} placeholder="50000" className="mt-1.5" />
                        </div>
                        <div>
                            <Label>Max capital (FJ$)</Label>
                            <Input data-testid="investor-max" type="number" value={investorForm.capital_max_fjd} onChange={(e) => setInvestorForm(f => ({ ...f, capital_max_fjd: e.target.value }))} placeholder="2000000" className="mt-1.5" />
                        </div>
                        <div>
                            <Label>Risk profile</Label>
                            <Select value={investorForm.risk_profile} onValueChange={(v) => setInvestorForm(f => ({ ...f, risk_profile: v }))}>
                                <SelectTrigger data-testid="investor-risk" className="mt-1.5"><SelectValue /></SelectTrigger>
                                <SelectContent>{RISK_PROFILES.map((r) => <SelectItem key={r.id} value={r.id}>{r.label} — {r.desc}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Investment horizon</Label>
                            <Select value={investorForm.investment_horizon} onValueChange={(v) => setInvestorForm(f => ({ ...f, investment_horizon: v }))}>
                                <SelectTrigger data-testid="investor-horizon" className="mt-1.5"><SelectValue /></SelectTrigger>
                                <SelectContent>{HORIZONS.map((h) => <SelectItem key={h.id} value={h.id}>{h.label}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="mb-5">
                        <Label className="mb-2 block">Interest tags <span className="text-muted-foreground font-normal text-xs">(select all that apply)</span></Label>
                        <div className="grid sm:grid-cols-2 gap-2" data-testid="investor-interests">
                            {INVESTOR_INTEREST_TAGS.map((t) => {
                                const selected = investorForm.interests.includes(t.id);
                                const Icon = t.icon;
                                return (
                                    <label key={t.id} data-testid={`interest-${t.id}`} className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition ${selected ? "border-coral bg-coral/5" : "border-border hover:border-muted-foreground/40"}`}>
                                        <Checkbox checked={selected} onCheckedChange={() => toggleInterest(t.id)} className="mt-0.5" />
                                        <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${t.accent}`} />
                                        <div className="min-w-0">
                                            <div className="font-app font-medium text-sm">{t.label}</div>
                                            <div className="text-xs text-muted-foreground font-app">{t.desc}</div>
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    </div>

                    <div className="mb-5">
                        <Label className="mb-2 block">Geographic focus <span className="text-muted-foreground font-normal text-xs">(islands you'd invest on)</span></Label>
                        <div className="flex flex-wrap gap-2" data-testid="investor-geo">
                            {FIJI_ISLANDS.map((island) => {
                                const selected = investorForm.geo_focus.includes(island);
                                return (
                                    <button
                                        type="button"
                                        key={island}
                                        onClick={() => toggleGeo(island)}
                                        data-testid={`geo-${island.replace(/\s+/g, "-")}`}
                                        className={`px-3 py-1.5 rounded-full text-xs font-app transition ${selected ? "bg-fiji-green text-white" : "bg-secondary text-foreground hover:bg-secondary/80"}`}
                                    >
                                        {island}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="mb-5">
                        <Label>Investor notes <span className="text-muted-foreground font-normal text-xs">(seen only by Bula admin)</span></Label>
                        <Textarea data-testid="investor-notes" value={investorForm.investor_notes} onChange={(e) => setInvestorForm(f => ({ ...f, investor_notes: e.target.value }))} rows={2} placeholder="e.g. 'Active fund — diligence within 30 days. Strong preference for tourism Joint Ventures.'" className="mt-1.5" />
                    </div>

                    <label className="flex items-center gap-2 mb-5 cursor-pointer">
                        <Checkbox checked={investorForm.accredited} onCheckedChange={(v) => setInvestorForm(f => ({ ...f, accredited: !!v }))} data-testid="investor-accredited" />
                        <span className="font-app text-sm">I am an accredited / sophisticated investor</span>
                    </label>

                    <div className="flex justify-end">
                        <Button onClick={saveInvestor} disabled={savingInvestor} data-testid="save-investor-btn" className="bg-coral text-white">
                            <Save className="h-4 w-4 mr-2" />
                            {savingInvestor ? "Saving…" : "Save investor profile"}
                        </Button>
                    </div>
                </Card>
            )}
        </div>
    );
}
